import { ReactNode } from 'react';
import { Trans } from '@lingui/react/macro';
import { endOfDay, isBefore, parseISO } from 'date-fns';

import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconRefusal from '@/icons/Refusal.svg';
import IconValidation from '@/icons/Validation.svg';
import { formatDate } from '@/utils/date';

const iconStyle = { fontSize: '1rem', color: 'var(--color-text-secondary-black)' } as const;

export const shouldShowWorkshopWarranty = (
  articleActions: Array<{ workshopWarrantyExpirationDate?: string | null }>
): boolean => articleActions.some((action) => !!action.workshopWarrantyExpirationDate);

export const WorkshopWarrantyExpirationDate = ({
  workshopWarrantyExpirationDate,
  showIcon = true,
}: {
  workshopWarrantyExpirationDate: string | null;
  showIcon?: boolean;
}) => {
  if (!workshopWarrantyExpirationDate) {
    return (
      <WorkshopWarrantyLine showIcon={showIcon}>
        <Trans id="action-card-header.workshop-warranty-expiration-date.out-of-warranty">
          Out of workshop warranty
        </Trans>
      </WorkshopWarrantyLine>
    );
  }

  const expirationDate = parseISO(workshopWarrantyExpirationDate);
  const isWorkshopWarrantyStillValid = !isBefore(endOfDay(expirationDate), new Date());

  if (isWorkshopWarrantyStillValid) {
    const workshopWarrantyExpirationDateFormatted = formatDate(expirationDate, {
      dateStyle: 'medium',
    });

    return (
      <WorkshopWarrantyLine showIcon={showIcon} icon={<IconValidation style={iconStyle} />}>
        <Trans id="action-card-header.workshop-warranty-expiration-date.valid">
          Workshop warranty until {workshopWarrantyExpirationDateFormatted}
        </Trans>
      </WorkshopWarrantyLine>
    );
  }

  return (
    <WorkshopWarrantyLine showIcon={showIcon}>
      <Trans id="action-card-header.workshop-warranty-expiration-date.expired">
        Workshop warranty expired
      </Trans>
    </WorkshopWarrantyLine>
  );
};

const WorkshopWarrantyLine = ({
  children,
  showIcon,
  icon = <IconRefusal style={iconStyle} />,
}: {
  children: ReactNode;
  showIcon: boolean;
  icon?: ReactNode;
}) => (
  <Stack row gap="0.25rem" alignItems="flex-start" flexWrap="nowrap">
    {showIcon && icon}
    <Paragraph size="200" color="secondary">
      {children}
    </Paragraph>
  </Stack>
);
